// ADM-1 (2026-06-12) — Requisições pro administrativo.
// A equipe abre pedidos de: viagem de campo (hotel/alimentação/veículo),
// suprimentos de escritório, manutenção patrimonial. Os pedidos viram
// tickets no kanban do módulo Administrativo (assistente + gerente recebem
// alerta no sino + email). Comunicação pelo próprio ticket (comentários).
// ADM-4: edição auditada (dono ou admin; diff vira evento na thread) e
// exclusão pelo dono com motivo pré-definido (soft delete → cancelado).

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import {
  App as AntApp, AutoComplete, Button, Card, Checkbox, DatePicker, Drawer, Empty, Flex, Form,
  Input, InputNumber, Modal, Radio, Select, Space, Spin, Table, Tag, Typography, Divider, Upload,
} from 'antd';
import { DeleteOutlined, EditOutlined, PaperClipOutlined, PlusOutlined, ReloadOutlined, SendOutlined, UploadOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { API_URL } from '../config';
import { getToken } from '../utils/auth';
import { fmtMomentoBr } from '../utils/dataFmt';
import { CATEGORIAS_SUGERIDAS, OPCOES_CONDICAO, OPCOES_FORMA_PAGAMENTO } from '../utils/despesaOpcoes';
import { opcoesFornecedorTexto, filtrarFornecedor, formatarCnpjCpf } from '../utils/fornecedores';
import { gerarVencimentos, conferirVencimentos, resumoVencimentos } from '../utils/vencimentosParcelas';
import type { RitmoVencimento } from '../utils/vencimentosParcelas';

const { Text, Title } = Typography;
const { TextArea } = Input;

export const TICKET_STATUS_CFG: Record<string, { label: string; cor: string; hex: string }> = {
  novo:         { label: 'Novo',          cor: 'blue',    hex: '#1677ff' },
  em_andamento: { label: 'Em andamento',  cor: 'gold',    hex: '#B07500' },
  aguardando:   { label: 'Aguardando',    cor: 'purple',  hex: '#722ed1' },
  concluido:    { label: 'Concluído',     cor: 'success', hex: '#389e0d' },
  cancelado:    { label: 'Cancelado',     cor: 'red',     hex: '#a8071a' },
};
export const TICKET_TIPO_CFG: Record<string, { label: string; emoji: string }> = {
  viagem:      { label: 'Viagem de campo',        emoji: '🧳' },
  suprimentos: { label: 'Suprimentos',            emoji: '📦' },
  manutencao:  { label: 'Manutenção patrimonial', emoji: '🔧' },
  aviso:       { label: 'Compra / Contratação', emoji: '🧾' },
  ouvidoria:   { label: 'Ouvidoria',              emoji: '📢' },
};
export const TICKET_PRIORIDADE_CFG: Record<string, { label: string; cor: string }> = {
  baixa:   { label: 'Baixa',   cor: 'default' },
  media:   { label: 'Média',   cor: 'blue' },
  alta:    { label: 'Alta',    cor: 'orange' },
  urgente: { label: 'Urgente', cor: 'red' },
};
// Espelha MOTIVOS_EXCLUSAO do backend (handlers/tickets.js)
export const MOTIVOS_EXCLUSAO_TICKET: Record<string, string> = {
  nao_necessario: 'Não preciso mais',
  duplicado:      'Pedido duplicado',
  mudou_escopo:   'O escopo mudou — vou abrir outro pedido',
  resolvido_fora: 'Já foi resolvido por outro meio',
  engano:         'Aberto por engano',
};

// Opções do Select de categoria no pedido de Compra/Contratação.
// A lista curta é intersectada com o cadastro: categoria renomeada ou
// desativada some da lista, em vez de gravar um nome que o Contas a Pagar não
// reconhece. Cadastro fora do ar cai na lista crua — melhor que um select vazio.
function opcoesCategoria(categorias: any[], avisoTipo: string, todas: boolean) {
  if (todas) {
    return categorias.map((c: any) => ({
      label: `${c.linha_dre} (${c.grupo_dre})`, value: c.linha_dre,
    }));
  }
  const sugeridas = CATEGORIAS_SUGERIDAS[avisoTipo] || [];
  if (!categorias.length) return sugeridas.map(n => ({ label: n, value: n }));
  const existentes = new Set(categorias.map((c: any) => c.linha_dre));
  return sugeridas.filter(n => existentes.has(n)).map(n => ({ label: n, value: n }));
}

// Shape mínimo que o DrawerPedido precisa pra editar (as duas páginas atendem)
export interface TicketEditavel {
  id: string;
  tipo: string;
  protocolo?: string;
  titulo: string;
  descricao: string | null;
  prioridade: string;
  dados: any;
  participantes?: ParticipanteTicket[];
  participantes_versao?: number;
  sou_participante?: boolean;
  sou_dono?: boolean;
}

export interface UsuarioTicket {
  id: string;
  email: string;
  nome: string;
}

// Participante gravado no ticket. A lista de `adm_ticket_meus` traz só id e
// nome; o detalhe (`adm_ticket_obter`) traz também o e-mail.
export interface ParticipanteTicket {
  id: string;
  nome: string;
  email?: string | null;
}

// Opções do Select de colegas: contas ativas + quem já está gravado no ticket.
// Sem isso, um participante que ficou inativo aparece como UUID cru no Select.
// Só marca "(inativo)" quando a lista de contas ativas carregou de fato.
function opcoesColegas(ativos: UsuarioTicket[], gravados: ParticipanteTicket[] | undefined, ativosOk: boolean) {
  const vistos = new Set(ativos.map((u) => String(u.id).trim().toLowerCase()));
  const opcoes = ativos.map((u) => ({ value: u.id, label: `${u.nome} (${u.email})` }));
  for (const p of gravados || []) {
    const chave = String(p.id).trim().toLowerCase();
    if (vistos.has(chave)) continue;
    vistos.add(chave);
    const nome = p.nome || p.email || 'Pessoa';
    opcoes.push({
      value: p.id,
      label: `${nome}${p.email && p.nome ? ` (${p.email})` : ''}${ativosOk ? ' (inativo)' : ''}`,
    });
  }
  return opcoes;
}

interface Ticket extends TicketEditavel {
  subtipo: string | null;
  status: string;
  criado_em: string;
  atualizado_em: string;
  cancelado_motivo?: string | null;
  cancelado_por?: string | null;
  n_comentarios?: number;
}

// ─── Thread de comentários do ticket (compartilhada com o kanban admin) ─────
export function TicketComentarios({ ticketId, onChange }: { ticketId: string; onChange?: () => void }) {
  const { message } = AntApp.useApp();
  const [coments, setComents] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [texto, setTexto] = useState('');
  const [mencionadosIds, setMencionadosIds] = useState<string[]>([]);
  const [usuarios, setUsuarios] = useState<UsuarioTicket[]>([]);
  const [enviando, setEnviando] = useState(false);

  const carregar = useCallback(async () => {
    setLoading(true);
    try {
      const token = await getToken();
      const r = await axios.post(`${API_URL}/comercial/propostas-geradas`,
        { action: 'adm_ticket_obter', id: ticketId },
        { headers: { Authorization: token } });
      const d = r.data.body ? JSON.parse(r.data.body) : r.data;
      setComents(d.comentarios || []);
    } catch { /* silencioso */ }
    finally { setLoading(false); }
  }, [ticketId]);
  useEffect(() => { carregar(); }, [carregar]);
  useEffect(() => {
    (async () => {
      try {
        const token = await getToken();
        const r = await axios.post(`${API_URL}/comercial/propostas-geradas`,
          { action: 'adm_ticket_usuarios' }, { headers: { Authorization: token } });
        const d = r.data.body ? JSON.parse(r.data.body) : r.data;
        setUsuarios(Array.isArray(d) ? d : []);
      } catch { /* o comentário continua disponível sem menção */ }
    })();
  }, []);

  async function enviar() {
    if (!texto.trim()) return;
    setEnviando(true);
    try {
      const token = await getToken();
      await axios.post(`${API_URL}/comercial/propostas-geradas`,
        { action: 'adm_ticket_comentar', id: ticketId, texto: texto.trim(), mencionados_ids: mencionadosIds },
        { headers: { Authorization: token } });
      setTexto(''); setMencionadosIds([]);
      await carregar();
      onChange?.();
    } catch (e: any) {
      message.error('Falha: ' + (e.response?.data?.error || e.message));
    } finally { setEnviando(false); }
  }

  return (
    <div>
      <Text strong style={{ fontSize: 12, display: 'block', marginBottom: 8 }}>💬 Comunicação</Text>
      {loading ? <Spin size="small" /> : coments.length === 0 ? (
        <Text type="secondary" italic style={{ fontSize: 11 }}>Nenhuma mensagem ainda.</Text>
      ) : (
        <Space direction="vertical" size={6} style={{ width: '100%', marginBottom: 8 }}>
          {coments.map((c: any) => <ComentarioCard key={c.id} c={c} />)}
        </Space>
      )}
      <Flex gap={6} style={{ marginTop: 8 }}>
        <TextArea rows={2} value={texto} onChange={e => setTexto(e.target.value)}
          placeholder="Escreva uma mensagem…" style={{ flex: 1 }} />
        <Button type="primary" icon={<SendOutlined />} loading={enviando}
          onClick={enviar} disabled={!texto.trim()} />
      </Flex>
      <Select mode="multiple" allowClear size="small" value={mencionadosIds}
        onChange={setMencionadosIds} optionFilterProp="label"
        placeholder="🔔 Avisar colegas selecionados (opcional)"
        style={{ width: '100%', marginTop: 6 }}
        options={usuarios.map((u) => ({ value: u.id, label: `${u.nome} (${u.email})` }))} />
    </div>
  );
}

// Um comentário (humano) ou evento de auditoria (edição/exclusão) na thread
// Texto livre com URLs viradas em links clicáveis e ENCURTADOS (Lucas
// 2026-07-16): "https://loja.com/produto/123?utm=..." exibe como
// "🔗 loja.com/produto/123…" abrindo em nova aba. Usado nas observações do
// pedido (solicitante e kanban admin) e nos comentários da thread.
export function TextoComLinks({ texto, style }: { texto: string; style?: any }) {
  const partes = String(texto).split(/(https?:\/\/[^\s<>"']+)/g);
  const encurtar = (url: string) => {
    try {
      const u = new URL(url);
      const label = u.hostname.replace(/^www\./, '') + (u.pathname + u.search).replace(/\/$/, '');
      return label.length > 42 ? label.slice(0, 40) + '…' : label;
    } catch { return url.length > 42 ? url.slice(0, 40) + '…' : url; }
  };
  return (
    <Text style={{ fontSize: 12, whiteSpace: 'pre-wrap', ...style }}>
      {partes.map((p, i) => /^https?:\/\//.test(p)
        ? <a key={i} href={p} target="_blank" rel="noopener noreferrer">🔗 {encurtar(p)}</a>
        : p)}
    </Text>
  );
}

export function ComentarioCard({ c }: { c: any }) {
  if (c.tipo === 'evento') {
    return (
      <div style={{ textAlign: 'center', padding: '2px 8px' }}>
        <Text type="secondary" style={{ fontSize: 11, fontStyle: 'italic', whiteSpace: 'pre-wrap' }}>
          {c.texto}
        </Text>
        <Text type="secondary" style={{ fontSize: 9, display: 'block' }}>{fmtMomentoBr(c.criado_em)}</Text>
      </div>
    );
  }
  return (
    <Card size="small"
      style={{ background: c.interno ? '#FFF8E1' : '#FAFBFF' }}
      bodyStyle={{ padding: '6px 10px' }}>
      <Flex justify="space-between" gap={8}>
        <Text strong style={{ fontSize: 11 }}>
          {c.autor_nome || (c.autor_email ? c.autor_email.split('@')[0] : '🕵️ Solicitante anônimo')}
          {c.interno && <Tag color="warning" style={{ fontSize: 9, marginLeft: 6 }}>nota interna</Tag>}
        </Text>
        <Text type="secondary" style={{ fontSize: 10 }}>{fmtMomentoBr(c.criado_em)}</Text>
      </Flex>
      <TextoComLinks texto={c.texto} />
      {Array.isArray(c.mencionados) && c.mencionados.length > 0 && (
        <Space size={4} wrap style={{ marginTop: 4 }}>
          {c.mencionados.map((u: UsuarioTicket) => <Tag key={u.id} color="blue">🔔 @{u.nome}</Tag>)}
        </Space>
      )}
    </Card>
  );
}

const UFS_BR = ['AC','AL','AP','AM','BA','CE','DF','ES','GO','MA','MT','MS','MG','PA','PB','PR','PE','PI','RJ','RN','RS','RO','RR','SC','SP','SE','TO'];

// ─── Drawer de criação/edição de pedido (ADM-4: também usado pelo kanban) ───
// Auto-contido: carrega os próprios catálogos (contratos, pessoas,
// equipamentos do patrimônio, materiais do estoque, municípios IBGE).
export function DrawerPedido({ aberto, ticket, onFechar, onSalvo }: {
  aberto: boolean;
  ticket?: TicketEditavel | null;
  onFechar: () => void;
  onSalvo: () => void;
}) {
  const { message } = AntApp.useApp();
  const [tipo, setTipo] = useState<'viagem' | 'suprimentos' | 'manutencao' | 'aviso'>('viagem');
  const [form] = Form.useForm();
  const [salvando, setSalvando] = useState(false);
  // Anexos (2026-07-16): cotações/orçamentos etc. Vivem em dados.anexos;
  // upload direto pro S3 via presigned URL (action adm_ticket_anexo_upload_url)
  const [anexos, setAnexos] = useState<Array<{ nome: string; s3_key: string; content_type?: string; tamanho?: number }>>([]);
  const [subindoAnexo, setSubindoAnexo] = useState(false);
  // Contratos pro Select de centro de custo (substitui o texto livre do MS
  // Forms, onde apareciam typos e "meio a meio dois projetos")
  const [contratos, setContratos] = useState<Array<{ id: string; numero: string; titulo: string }>>([]);
  // Pessoas pro "Quem realizará o campo?": usuários do sistema + profissionais
  // de campo. mode="tags" ainda permite texto livre (barqueiro avulso etc.)
  const [pessoas, setPessoas] = useState<string[]>([]);
  // Convites de viagem são sempre identidades Cognito. Diferente da equipe
  // operacional, não há texto livre: só quem foi escolhido aqui ganha acesso.
  const [usuariosTicket, setUsuariosTicket] = useState<UsuarioTicket[]>([]);
  const [usuariosTicketOk, setUsuariosTicketOk] = useState(false);
  // Equipamentos da empresa alocáveis em campo (patrimônio: veículos,
  // barcos/embarcações, equipamento eletrônico)
  const [equipamentos, setEquipamentos] = useState<Array<{ id: string; descricao: string; codigo_patrimonio: string | null; categoria: string | null; modelo: string | null }>>([]);
  // Catálogo do estoque de consumíveis (administrativo.materiais) pro form
  // de suprimentos — AutoComplete: escolhe da lista ou digita item avulso
  const [materiais, setMateriais] = useState<Array<{ nome: string; unidade: string | null; categoria: string | null }>>([]);
  // Destino por UF + município (IBGE) — substitui texto livre
  const [uf, setUf] = useState<string>('RS');
  const [municipios, setMunicipios] = useState<string[]>([]);
  const [carregandoMun, setCarregandoMun] = useState(false);
  // Campos condicionais do form de viagem (espelham o branching do MS Forms)
  const precisaHotel = Form.useWatch('precisa_hotel', form);
  const precisaVeiculo = Form.useWatch('precisa_veiculo', form);
  const entregaMatriz = Form.useWatch('entrega_matriz', form);
  // Compra de PRODUTO pede o nº de unidades explícito (Lucas 2026-07-16)
  const avisoTipo = Form.useWatch('aviso_tipo', form) ?? 'compra';
  // Condição de pagamento: só 'avista' esconde o nº de parcelas
  const condicao = Form.useWatch('condicao', form) ?? 'avista';
  // Categorias da DRE (2026-09-18): o pedido já traz a categoria pra Daniele
  // não ter que adivinhar no lançamento. A lista curta é a sugerida pelo tipo;
  // "ver todas" abre o cadastro inteiro pro caso que não estiver nela.
  const [categorias, setCategorias] = useState<any[]>([]);
  const [todasCategorias, setTodasCategorias] = useState(false);
  // Fornecedores do cadastro (2026-09-18): quem abre o pedido escolhe da lista
  // ou digita livre. Carrega SÓ quando o pedido é de Compra/Contratação — são
  // ~950 linhas, e quem pede uma viagem não precisa delas. O mount desta tela
  // já faz 5 chamadas.
  const [fornecedores, setFornecedores] = useState<any[]>([]);
  const fornPedidos = useRef(false);
  // Sem o memo, as ~950 opções são remontadas a cada tecla digitada em
  // QUALQUER campo do drawer — o formulário inteiro re-renderiza.
  const opcoesForn = useMemo(() => opcoesFornecedorTexto(fornecedores), [fornecedores]);
  // Participante já gravado que ficou inativo continua com nome no Select
  const opcoesParticipantes = useMemo(
    () => opcoesColegas(usuariosTicket, ticket?.participantes, usuariosTicketOk),
    [usuariosTicket, ticket?.participantes, usuariosTicketOk]);
  // Cronograma das parcelas escolhido no pedido.
  const [ritmo, setRitmo] = useState<RitmoVencimento>('mensal');
  const [vencimentos, setVencimentos] = useState<string[]>([]);
  const [vencManual, setVencManual] = useState(false);
  const [datasAbertas, setDatasAbertas] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const token = await getToken();
        const [rc, ru, re, rm, rcat, rut] = await Promise.allSettled([
          axios.get(`${API_URL}/mobile/contratos`, { headers: { Authorization: token } }),
          axios.post(`${API_URL}/comercial/propostas-geradas`,
            { action: 'op_usuario_dados_tecnicos_listar_cognito' },
            { headers: { Authorization: token } }),
          axios.post(`${API_URL}/comercial/propostas-geradas`,
            { action: 'adm_equipamentos_alocaveis' },
            { headers: { Authorization: token } }),
          axios.post(`${API_URL}/comercial/propostas-geradas`,
            { action: 'admin_listar_materiais' },
            { headers: { Authorization: token } }),
          axios.get(`${API_URL}/categorias`, { headers: { Authorization: token } }),
          axios.post(`${API_URL}/comercial/propostas-geradas`,
            { action: 'adm_ticket_usuarios' },
            { headers: { Authorization: token } }),
        ]);
        if (rc.status === 'fulfilled') {
          const d = rc.value.data.body ? JSON.parse(rc.value.data.body) : rc.value.data;
          setContratos(Array.isArray(d) ? d : (d.contratos || []));
        }
        const nomes = new Set<string>();
        if (ru.status === 'fulfilled') {
          const d = ru.value.data.body ? JSON.parse(ru.value.data.body) : ru.value.data;
          for (const u of (Array.isArray(d) ? d : [])) {
            if (u.nome) nomes.add(u.nome);
            else if (u.email) nomes.add(u.email.split('@')[0]);
          }
        }
        setPessoas(Array.from(nomes).sort((a, b) => a.localeCompare(b)));
        if (rut.status === 'fulfilled') {
          const d = rut.value.data.body ? JSON.parse(rut.value.data.body) : rut.value.data;
          setUsuariosTicket(Array.isArray(d) ? d : []);
          setUsuariosTicketOk(Array.isArray(d));
        }
        if (re.status === 'fulfilled') {
          const d = re.value.data.body ? JSON.parse(re.value.data.body) : re.value.data;
          setEquipamentos(Array.isArray(d) ? d : []);
        }
        if (rm.status === 'fulfilled') {
          const d = rm.value.data.body ? JSON.parse(rm.value.data.body) : rm.value.data;
          setMateriais(Array.isArray(d?.materiais) ? d.materiais : []);
        }
        if (rcat.status === 'fulfilled') {
          const d = rcat.value.data.body ? JSON.parse(rcat.value.data.body) : rcat.value.data;
          setCategorias(Array.isArray(d) ? d.filter((c: any) => c.linha_dre) : []);
        }
      } catch { /* opcional */ }
    })();
  }, []);

  // Fornecedores só quando o pedido é de Compra/Contratação: ~950 linhas não
  // têm por que viajar para quem está pedindo uma viagem de campo. Uma vez por
  // sessão do drawer (o ref evita recarregar a cada troca de tipo).
  useEffect(() => {
    if (tipo !== 'aviso' || fornPedidos.current) return;
    fornPedidos.current = true;
    (async () => {
      try {
        const token = await getToken();
        // simples=1: só id, nome, razão social e CNPJ. Quem abre um pedido não
        // precisa (e não deve receber) e-mail, telefone e observações de ~950
        // fornecedores — e a rede de campo agradece.
        const r = await axios.get(`${API_URL}/fornecedores?ativo=true&limit=2000&simples=1`,
          { headers: { Authorization: token } });
        const d = r.data.body ? JSON.parse(r.data.body) : r.data;
        setFornecedores(Array.isArray(d) ? d : []);
      } catch {
        // Sem a lista o campo continua aceitando texto livre — que é como era.
        fornPedidos.current = false;
      }
    })();
  }, [tipo]);

  // Cronograma sugerido: "todo mês no mesmo dia" a partir do vencimento. Mexeu
  // numa data, a sugestão para de reescrever (vencManual), e o botão desfaz.
  const dataVenc = Form.useWatch('data_vencimento', form);
  const nParcelasForm = Form.useWatch('parcelas', form);
  const primeiroVenc = dataVenc ? dayjs(dataVenc).format('YYYY-MM-DD') : '';
  const nParcelas = condicao === 'avista' ? 1 : (parseInt(String(nParcelasForm), 10) || 2);
  useEffect(() => {
    if (condicao === 'avista' || !primeiroVenc || nParcelas < 2) { setVencimentos([]); return; }
    if (vencManual && vencimentos.length === nParcelas) return;
    const gerado = gerarVencimentos(primeiroVenc, nParcelas, ritmo);
    setVencimentos(atual => (atual.join('|') === gerado.join('|') ? atual : gerado));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [condicao, primeiroVenc, nParcelas, ritmo, vencManual]);

  // Municípios da UF via API pública do IBGE (com busca no Select)
  useEffect(() => {
    if (!uf) return;
    setCarregandoMun(true);
    fetch(`https://servicodados.ibge.gov.br/api/v1/localidades/estados/${uf}/municipios?orderBy=nome`)
      .then(r => r.json())
      .then((d) => setMunicipios(Array.isArray(d) ? d.map((m: any) => m.nome) : []))
      .catch(() => setMunicipios([]))
      .finally(() => setCarregandoMun(false));
  }, [uf]);

  // Reabre o form preenchido a partir do JSONB `dados` (edição — ADM-4)
  function dadosParaForm(t: TicketEditavel): any {
    const d = t.dados || {};
    const v: any = { descricao: t.descricao || undefined, prioridade: t.prioridade };
    // IDs gravados desde o ADM-4; fallback: match dos labels com a lista
    const ids = Array.isArray(d.contrato_ids) && d.contrato_ids.length ? d.contrato_ids
      : (d.centros_custo || [])
          .map((lbl: string) => contratos.find(c => `${c.numero} — ${c.titulo}` === lbl)?.id)
          .filter(Boolean);
    if (t.tipo === 'viagem') {
      const [cid, ufd] = String(d.destino || '').split('/');
      if (ufd) setUf(ufd);
      v.contratos = ids;
      v.destino_cidade = cid || undefined;
      if (d.data_ida && d.data_volta) v.periodo = [dayjs(d.data_ida), dayjs(d.data_volta)];
      v.equipe = d.equipe ? String(d.equipe).split(', ') : [];
      v.participantes_ids = Array.isArray(t.participantes)
        ? t.participantes.map((p: ParticipanteTicket) => p.id)
        : (Array.isArray(d.participantes_ids) ? d.participantes_ids : []);
      v.equipamentos = d.equipamentos || [];
      v.precisa_alimentacao = !!d.precisa_alimentacao;
      v.precisa_hotel = !!d.precisa_hotel;
      if (d.precisa_hotel) {
        v.hotel_noites = d.hotel_noites;
        v.hotel_cidade = d.hotel_cidade;
        v.hotel_outras_cidades = d.hotel_outras_cidades ? String(d.hotel_outras_cidades).split(', ') : [];
        v.hotel_sugestoes = Array.isArray(d.hotel_sugestoes) ? d.hotel_sugestoes
          : (d.hotel_sugestao ? [d.hotel_sugestao] : []);
      }
      v.precisa_veiculo = !!d.precisa_veiculo;
      if (d.precisa_veiculo) {
        const [lugar, agencia] = String(d.veiculo_retirada_local || '').split(' — ');
        v.veiculo_retirada_cidade = (lugar || '').split('/')[0] || undefined;
        v.veiculo_agencia = agencia || undefined;
        if (d.veiculo_retirada_data && d.veiculo_devolucao_data) {
          v.veiculo_periodo = [dayjs(d.veiculo_retirada_data), dayjs(d.veiculo_devolucao_data)];
        }
      }
    } else if (t.tipo === 'suprimentos') {
      v.titulo = t.titulo;
      v.itens_lista = Array.isArray(d.itens_lista) && d.itens_lista.length ? d.itens_lista
        : (d.itens ? String(d.itens).split('\n').map((s: string) => ({ nome: s })) : [{}]);
      v.contratos = ids;
      if (d.data_necessaria) v.data_necessaria = dayjs(d.data_necessaria);
      v.entrega_matriz = !!d.entrega_matriz;
      if (!d.entrega_matriz) {
        v.entrega_endereco = d.entrega_endereco || undefined;
        const [cidE, ufE] = String(d.entrega_cidade || '').split('/');
        if (ufE) setUf(ufE);
        v.entrega_cidade = cidE || undefined;
        v.entrega_cep = d.entrega_cep || undefined;
        v.entrega_referencia = d.entrega_referencia || undefined;
      }
    } else if (t.tipo === 'manutencao') {
      v.titulo = t.titulo;
      v.local = d.local;
      v.problema = d.problema;
    } else if (t.tipo === 'aviso') {
      v.titulo = t.titulo;
      v.aviso_tipo = d.aviso_tipo || 'compra';
      v.fornecedor = d.fornecedor;
      v.quantidade = d.quantidade != null ? parseInt(d.quantidade, 10) : undefined;
      v.valor = d.valor != null ? parseFloat(d.valor) : undefined;
      if (d.data_compra) v.data_compra = dayjs(d.data_compra);
      if (d.data_vencimento) v.data_vencimento = dayjs(d.data_vencimento);
      v.categoria_dre = d.categoria_dre || undefined;
      v.condicao = d.condicao || 'avista';
      v.parcelas = d.parcelas != null ? parseInt(d.parcelas, 10) : 2;
      v.forma_pagamento = d.forma_pagamento || undefined;
      v.pagamento_dados = d.pagamento_dados || undefined;
      // Sem reidratar, editar o pedido só para corrigir um typo APAGARIA o
      // vínculo com o cadastro: o salvar remonta o `dados` inteiro.
      v.fornecedor_id = d.fornecedor_id || undefined;
      v.fornecedor_cnpj = d.fornecedor_cnpj || undefined;
      v.contratos = ids;
    }
    return v;
  }

  useEffect(() => {
    if (!aberto) return;
    form.resetFields();
    setAnexos(Array.isArray(ticket?.dados?.anexos) ? ticket!.dados.anexos : []);
    if (ticket) {
      setTipo((ticket.tipo as any) || 'viagem');
      form.setFieldsValue(dadosParaForm(ticket));
      // Cronograma já escolhido: reabrir não pode regerar por cima dele.
      const vs = Array.isArray(ticket.dados?.vencimentos) ? ticket.dados.vencimentos : [];
      setVencimentos(vs.map(String));
      setVencManual(vs.length > 0);
    } else {
      setVencimentos([]);
      setVencManual(false);
    }
    setDatasAbertas(false);
    setRitmo('mensal');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [aberto, ticket?.id]);

  // Pré-preenche hospedagem/veículo com o destino e o período do campo
  // (só quando os campos ainda estão vazios — edição do usuário é respeitada)
  // Noites de hospedagem = dias do período − 1 (ida 10 → volta 12 = 2 noites)
  function noitesDoPeriodo(periodo?: any[]): number | null {
    if (!periodo?.[0] || !periodo?.[1]) return null;
    const n = periodo[1].diff(periodo[0], 'day');
    return n >= 1 ? n : null;
  }
  function aoMarcarHotel(marcado: boolean) {
    if (!marcado) return;
    const cidade = form.getFieldValue('destino_cidade');
    const patch: any = {};
    if (cidade && !form.getFieldValue('hotel_cidade')) patch.hotel_cidade = cidade;
    const noites = noitesDoPeriodo(form.getFieldValue('periodo'));
    if (noites && !form.getFieldValue('hotel_noites')) patch.hotel_noites = noites;
    if (Object.keys(patch).length) form.setFieldsValue(patch);
  }
  // Noites é valor derivado: recalcula sempre que o período do campo muda
  function aoMudarPeriodo(periodo: any) {
    const noites = noitesDoPeriodo(periodo);
    if (noites) form.setFieldsValue({ hotel_noites: noites });
  }
  function aoMarcarVeiculo(marcado: boolean) {
    if (!marcado) return;
    const cidade = form.getFieldValue('destino_cidade');
    const periodo = form.getFieldValue('periodo');
    const patch: any = {};
    if (cidade && !form.getFieldValue('veiculo_retirada_cidade')) {
      patch.veiculo_retirada_cidade = cidade;
    }
    if (periodo && !form.getFieldValue('veiculo_periodo')) {
      patch.veiculo_periodo = periodo;
    }
    if (Object.keys(patch).length) form.setFieldsValue(patch);
  }

  async function salvar(values: any) {
    setSalvando(true);
    try {
      const dados: any = {};
      // Centros de custo: nomes legíveis pro card do administrativo
      const nomeContrato = (id: string) => {
        const c = contratos.find(x => x.id === id);
        return c ? `${c.numero} — ${c.titulo}` : id;
      };
      if (tipo === 'viagem') {
        dados.destino = `${values.destino_cidade}/${uf}`;
        dados.data_ida = values.periodo?.[0]?.format('YYYY-MM-DD');
        dados.data_volta = values.periodo?.[1]?.format('YYYY-MM-DD');
        dados.equipe = (values.equipe || []).join(', ');
        dados.equipamentos = values.equipamentos || [];
        dados.centros_custo = (values.contratos || []).map(nomeContrato);
        dados.contrato_ids = values.contratos || [];
        dados.precisa_alimentacao = !!values.precisa_alimentacao;
        dados.precisa_hotel = !!values.precisa_hotel;
        if (values.precisa_hotel) {
          dados.hotel_noites = values.hotel_noites;
          dados.hotel_cidade = values.hotel_cidade;
          dados.hotel_outras_cidades = (values.hotel_outras_cidades || []).join(', ') || null;
          dados.hotel_sugestoes = (values.hotel_sugestoes || []).filter(Boolean);
        }
        dados.precisa_veiculo = !!values.precisa_veiculo;
        if (values.precisa_veiculo) {
          dados.veiculo_retirada_local = `${values.veiculo_retirada_cidade}/${uf}`
            + (values.veiculo_agencia ? ` — ${values.veiculo_agencia}` : '');
          dados.veiculo_retirada_data = values.veiculo_periodo?.[0]?.format('YYYY-MM-DD');
          dados.veiculo_devolucao_data = values.veiculo_periodo?.[1]?.format('YYYY-MM-DD');
        }
      } else if (tipo === 'suprimentos') {
        // Itens estruturados (catálogo do estoque ou texto livre) + string
        // legível em dados.itens pra compatibilidade com tickets antigos
        dados.itens_lista = (values.itens_lista || [])
          .filter((it: any) => it?.nome)
          .map((it: any) => ({
            nome: it.nome,
            qtd: it.qtd || null,
            unidade: materiais.find(m => m.nome === it.nome)?.unidade || null,
          }));
        dados.itens = dados.itens_lista
          .map((it: any) => `${it.qtd ? `${it.qtd}${it.unidade ? ` ${it.unidade}` : 'x'} — ` : ''}${it.nome}`)
          .join('\n');
        dados.centros_custo = (values.contratos || []).map(nomeContrato);
        dados.contrato_ids = values.contratos || [];
        dados.data_necessaria = values.data_necessaria?.format('YYYY-MM-DD');
        dados.entrega_matriz = !!values.entrega_matriz;
        if (values.entrega_matriz) {
          dados.local_entrega = 'Matriz da empresa';
        } else {
          dados.entrega_endereco = values.entrega_endereco || null;
          dados.entrega_cidade = values.entrega_cidade ? `${values.entrega_cidade}/${uf}` : null;
          dados.entrega_cep = values.entrega_cep || null;
          dados.entrega_referencia = values.entrega_referencia || null;
        }
      } else if (tipo === 'manutencao') {
        dados.local = values.local;
        dados.problema = values.problema;
      } else if (tipo === 'aviso') {
        dados.aviso_tipo = values.aviso_tipo;     // compra | aluguel | contratacao
        dados.fornecedor = values.fornecedor;
        // Unidades só fazem sentido pra compra de produto
        dados.quantidade = values.aviso_tipo === 'compra' ? (values.quantidade || null) : null;
        dados.valor = values.valor;
        dados.data_compra = values.data_compra?.format('YYYY-MM-DD');
        dados.data_vencimento = values.data_vencimento?.format('YYYY-MM-DD') || null;
        dados.categoria_dre = values.categoria_dre || null;
        // Mesmos três modos do Contas a Pagar — o lançamento é criado a partir
        // daqui sem ninguém reinterpretar "parcelado" como "mensal".
        dados.condicao = values.condicao || 'avista';
        dados.parcelas = values.condicao && values.condicao !== 'avista'
          ? (values.parcelas || 2) : null;
        dados.forma_pagamento = values.forma_pagamento || null;
        dados.pagamento_dados = values.pagamento_dados || null;
        // Vínculo com o cadastro de fornecedores (quando a pessoa escolheu da
        // lista) + CNPJ informado. O nome continua em `dados.fornecedor`, do
        // mesmo jeito que já está gravado em produção.
        dados.fornecedor_id = values.fornecedor_id || null;
        dados.fornecedor_cnpj = values.fornecedor_cnpj || null;
        // Cronograma: uma data por parcela. Gravado SEMPRE que há parcela, para
        // o servidor não ter que adivinhar — cronograma é dado, não fórmula.
        // Mesma regua do pre-voo do servidor: recusar aqui evita o pedido
        // nascer com um cronograma que o lancamento depois nao aceita.
        // Sem data de vencimento nao ha cronograma: o vencimento so e
        // obrigatorio na contratacao NOVA, e uma compra parcelada sem ele tem
        // que continuar salvando (o servidor calcula as datas como sempre).
        const nVenc = values.condicao && values.condicao !== 'avista' && values.data_vencimento
          ? (values.parcelas || 0) : 0;
        if (nVenc >= 2) {
          const erroCron = conferirVencimentos(vencimentos, nVenc);
          if (erroCron) { message.warning(`Vencimentos: ${erroCron}`); return; }
        }
        dados.vencimentos = nVenc >= 2 ? vencimentos : null;
        dados.centros_custo = (values.contratos || []).map(nomeContrato);
        dados.contrato_ids = values.contratos || [];
      }
      // Anexos valem pra qualquer tipo de requisição
      dados.anexos = anexos;
      // Viagem não pede título: gera a partir dos contratos + destino
      // (ex: "HY26012 — Marau/RS")
      const titulo = tipo === 'viagem'
        ? `${(values.contratos || []).map((id: string) => contratos.find(c => c.id === id)?.numero || '').filter(Boolean).join('+') || 'Campo'} — ${values.destino_cidade}/${uf}`
        : values.titulo;
      const token = await getToken();
      const payloadBase = {
        titulo,
        descricao: values.descricao || null,
        prioridade: values.prioridade || 'media',
        contrato_id: values.contratos?.[0] || null,
        dados,
        // Resumos da lista da Central não trazem participantes. Ao editar
        // qualquer outro campo, omitir esta chave preserva os convites no
        // servidor; só envia depois de o detalhe ter hidratado a seleção.
        participantes_ids: tipo === 'viagem' && (!ticket || Array.isArray(ticket.participantes))
          ? (values.participantes_ids || []) : undefined,
        // A versão vem apenas no detalhe hidratado; sem ela a lista não é
        // enviada, preservando participantes alterados em outra aba.
        participantes_versao: tipo === 'viagem' && Array.isArray(ticket?.participantes)
          ? ticket?.participantes_versao : undefined,
      };
      if (ticket) {
        await axios.post(`${API_URL}/comercial/propostas-geradas`,
          { action: 'adm_ticket_editar', id: ticket.id, ...payloadBase },
          { headers: { Authorization: token } });
        message.success('Alterações salvas! A outra parte foi avisada e o histórico ficou na thread.', 5);
      } else {
        const r = await axios.post(`${API_URL}/comercial/propostas-geradas`,
          { action: 'adm_ticket_criar', tipo, ...payloadBase },
          { headers: { Authorization: token } });
        const d = r.data.body ? JSON.parse(r.data.body) : r.data;
        message.success(`Pedido ${d.protocolo || ''} aberto! O administrativo foi avisado.`, 6);
      }
      form.resetFields();
      onFechar();
      onSalvo();
    } catch (e: any) {
      if (e.response?.status === 409) {
        message.error('Os participantes foram alterados em outra aba. Recarregue o pedido antes de salvar para não sobrescrever a alteração.');
      } else {
        message.error('Falha: ' + (e.response?.data?.error || e.message));
      }
    } finally { setSalvando(false); }
  }

  return (
    <Drawer open={aberto} onClose={() => { form.resetFields(); onFechar(); }}
      title={ticket ? `✏️ Editar ${ticket.protocolo || 'pedido'}` : 'Novo pedido pro administrativo'}
      width={520} destroyOnClose>
      <Radio.Group value={tipo} onChange={e => setTipo(e.target.value)} disabled={!!ticket}
        buttonStyle="solid" style={{ marginBottom: 16 }}>
        <Radio.Button value="viagem">🧳 Viagem de campo</Radio.Button>
        <Radio.Button value="suprimentos">📦 Suprimentos</Radio.Button>
        <Radio.Button value="manutencao">🔧 Manutenção</Radio.Button>
        <Radio.Button value="aviso">🧾 Compra / Contratação</Radio.Button>
      </Radio.Group>

      <Form form={form} layout="vertical" size="small" onFinish={salvar}>
        {tipo !== 'viagem' && (
          <Form.Item label="Título do pedido" name="titulo"
            rules={[{ required: true, message: 'Dê um título curto' }]}>
            <Input placeholder={tipo === 'suprimentos' ? 'Ex: Reposição papel toalha + café'
              : tipo === 'aviso' ? 'Ex: Aluguel de bote pro campo P26012'
              : 'Ex: Lâmpada queimada na sala de reuniões'} />
          </Form.Item>
        )}

        {tipo === 'viagem' && (
          <>
            {/* Centro de custo no topo — é o "título" do pedido */}
            <Form.Item label="Centro de custo (contratos)" name="contratos"
              rules={[{ required: true, message: 'Vincule pelo menos um contrato' }]}
              extra="Pode selecionar mais de um quando o campo atende vários projetos">
              <Select mode="multiple" showSearch optionFilterProp="label" allowClear
                placeholder="Buscar contrato…"
                options={contratos.map(c => ({ value: c.id, label: `${c.numero} — ${c.titulo}` }))} />
            </Form.Item>
            <Flex gap={12}>
              <Form.Item label="UF" style={{ width: 90 }}>
                <Select value={uf} onChange={setUf} showSearch
                  options={UFS_BR.map(u => ({ value: u, label: u }))} />
              </Form.Item>
              <Form.Item label="Cidade do campo" name="destino_cidade" style={{ flex: 1 }}
                rules={[{ required: true, message: 'Selecione a cidade' }]}>
                <Select showSearch optionFilterProp="value" loading={carregandoMun}
                  placeholder="Buscar município…"
                  options={municipios.map(m => ({ value: m, label: m }))} />
              </Form.Item>
            </Flex>
            <Form.Item label="Período do campo" name="periodo"
              rules={[{ required: true, message: 'Informe ida e volta' }]}>
              <DatePicker.RangePicker format="DD/MM/YYYY" style={{ width: '100%' }}
                onChange={aoMudarPeriodo} />
            </Form.Item>
            <Form.Item label="Quem realizará o campo?" name="equipe"
              rules={[{ required: true, message: 'Informe a equipe' }]}
              extra="Selecione da lista (usuários + profissionais de campo) ou digite livre — ex: barqueiro com valor">
              <Select mode="tags" showSearch placeholder="Buscar pessoa ou digitar…"
                options={pessoas.map(p => ({ value: p, label: p }))} />
            </Form.Item>
            <Form.Item label="Colegas que podem acompanhar este pedido" name="participantes_ids"
              extra="Selecione pessoas da empresa. Elas verão este pedido e poderão responder, sem acesso à gestão.">
              <Select mode="multiple" showSearch optionFilterProp="label" allowClear
                placeholder="Buscar colega…"
                options={opcoesParticipantes} />
            </Form.Item>
            <Form.Item label="Equipamentos da empresa a alocar" name="equipamentos"
              extra="Veículos, barcos e equipamentos eletrônicos do patrimônio">
              <Select mode="multiple" showSearch optionFilterProp="label" allowClear
                placeholder="Buscar equipamento…"
                options={Array.from(new Set(equipamentos.map(e => e.categoria || 'Outros'))).map(cat => ({
                  label: cat,
                  options: equipamentos.filter(e => (e.categoria || 'Outros') === cat).map(e => {
                    const rotulo = `${e.descricao}${e.modelo ? ` ${e.modelo}` : ''}${e.codigo_patrimonio ? ` [${e.codigo_patrimonio}]` : ''}`;
                    return { value: rotulo, label: rotulo };
                  }),
                }))} />
            </Form.Item>

            <Form.Item name="precisa_alimentacao" valuePropName="checked" style={{ marginBottom: 4 }}>
              <Checkbox>🍽️ Provisionar alimentação</Checkbox>
            </Form.Item>
            <Form.Item name="precisa_hotel" valuePropName="checked" style={{ marginBottom: 4 }}>
              <Checkbox onChange={(e) => aoMarcarHotel(e.target.checked)}>
                🏨 Há necessidade de hospedagem?
              </Checkbox>
            </Form.Item>
            {precisaHotel && (
              <Card size="small" style={{ marginBottom: 12, background: '#FAFBFF' }} bodyStyle={{ padding: 10 }}>
                <Flex gap={12}>
                  <Form.Item label="Quantas noites?" name="hotel_noites" style={{ width: 110 }}
                    rules={[{ required: true, message: 'Noites?' }]}>
                    <InputNumber min={1} max={60} style={{ width: '100%' }} />
                  </Form.Item>
                  <Form.Item label="Cidade principal para reserva" name="hotel_cidade" style={{ flex: 1 }}
                    rules={[{ required: true, message: 'Cidade?' }]}
                    extra="Pré-preenchida com a cidade do campo — pode trocar">
                    <Select showSearch optionFilterProp="value" loading={carregandoMun}
                      placeholder="Buscar município…"
                      options={municipios.map(m => ({ value: m, label: m }))} />
                  </Form.Item>
                </Flex>
                <Form.Item label="Outras opções de cidade" name="hotel_outras_cidades">
                  <Select mode="tags" showSearch placeholder="Buscar municípios ou digitar…"
                    options={municipios.map(m => ({ value: m, label: m }))} />
                </Form.Item>
                <Form.Item label="Sugestões de local (hotel/Airbnb)" style={{ marginBottom: 0 }}>
                  <Form.List name="hotel_sugestoes">
                    {(fields, { add, remove }) => (
                      <Space direction="vertical" size={6} style={{ width: '100%' }}>
                        {fields.map((field) => (
                          <Flex key={field.key} gap={6}>
                            <Form.Item {...field} noStyle>
                              <Input placeholder="Nome do hotel, link do Airbnb ou observação" />
                            </Form.Item>
                            <Button size="small" type="text" danger
                              onClick={() => remove(field.name)}>×</Button>
                          </Flex>
                        ))}
                        <Button size="small" type="dashed" block onClick={() => add('')}>
                          + Adicionar sugestão
                        </Button>
                      </Space>
                    )}
                  </Form.List>
                </Form.Item>
              </Card>
            )}
            <Form.Item name="precisa_veiculo" valuePropName="checked" style={{ marginBottom: 4 }}>
              <Checkbox onChange={(e) => aoMarcarVeiculo(e.target.checked)}>
                🚙 Há necessidade de aluguel de veículo?
              </Checkbox>
            </Form.Item>
            {precisaVeiculo && (
              <Card size="small" style={{ marginBottom: 12, background: '#FAFBFF' }} bodyStyle={{ padding: 10 }}>
                <Flex gap={12}>
                  <Form.Item label="Cidade de retirada" name="veiculo_retirada_cidade" style={{ flex: 1 }}
                    rules={[{ required: true, message: 'Onde retirar?' }]}
                    extra="Pré-preenchida com a cidade do campo — pode trocar">
                    <Select showSearch optionFilterProp="value" loading={carregandoMun}
                      placeholder="Buscar município…"
                      options={municipios.map(m => ({ value: m, label: m }))} />
                  </Form.Item>
                  <Form.Item label="Agência (se souber)" name="veiculo_agencia" style={{ flex: 1 }}>
                    <Input placeholder="Ex: Localiza Centro" />
                  </Form.Item>
                </Flex>
                <Form.Item label="Retirada e devolução" name="veiculo_periodo" style={{ marginBottom: 0 }}
                  rules={[{ required: true, message: 'Datas?' }]}
                  extra="Pré-preenchidas com o período do campo — ajuste se necessário">
                  <DatePicker.RangePicker format="DD/MM/YYYY" style={{ width: '100%' }} />
                </Form.Item>
              </Card>
            )}
          </>
        )}

        {tipo === 'suprimentos' && (
          <>
            <Form.Item label="Itens" required style={{ marginBottom: 12 }}
              extra="Busque no catálogo do estoque ou digite um item avulso">
              <Form.List name="itens_lista" initialValue={[{}]}>
                {(fields, { add, remove }) => (
                  <Space direction="vertical" size={6} style={{ width: '100%' }}>
                    {fields.map((field) => (
                      <Flex key={field.key} gap={6}>
                        <Form.Item name={[field.name, 'nome']} style={{ flex: 1, marginBottom: 0 }}
                          rules={[{ required: true, message: 'Qual item?' }]}>
                          <AutoComplete placeholder="Buscar item ou digitar…"
                            options={materiais.map(m => ({
                              value: m.nome,
                              label: `${m.nome}${m.unidade ? ` (${m.unidade})` : ''}`,
                            }))}
                            filterOption={(input, opt) =>
                              String(opt?.value || '').toLowerCase().includes(input.toLowerCase())} />
                        </Form.Item>
                        <Form.Item name={[field.name, 'qtd']} style={{ width: 90, marginBottom: 0 }}>
                          <InputNumber min={1} placeholder="Qtd" style={{ width: '100%' }} />
                        </Form.Item>
                        {fields.length > 1 && (
                          <Button size="small" type="text" danger
                            onClick={() => remove(field.name)}>×</Button>
                        )}
                      </Flex>
                    ))}
                    <Button size="small" type="dashed" block onClick={() => add({})}>
                      + Adicionar item
                    </Button>
                  </Space>
                )}
              </Form.List>
            </Form.Item>
            <Form.Item label="Centro de custo (contratos)" name="contratos"
              extra="Opcional — vincule quando a compra for de um projeto específico">
              <Select mode="multiple" showSearch optionFilterProp="label" allowClear
                placeholder="Buscar contrato…"
                options={contratos.map(c => ({ value: c.id, label: `${c.numero} — ${c.titulo}` }))} />
            </Form.Item>
            <Form.Item label="Necessário até" name="data_necessaria"
              rules={[{ required: true, message: 'Quando precisa ter o item disponível?' }]}
              extra="Data em que o item precisa estar disponível">
              <DatePicker format="DD/MM/YYYY" style={{ width: 200 }} />
            </Form.Item>
            <Form.Item name="entrega_matriz" valuePropName="checked" style={{ marginBottom: 4 }}>
              <Checkbox>🏢 Entregar na matriz da empresa</Checkbox>
            </Form.Item>
            {!entregaMatriz && (
              <Card size="small" style={{ marginBottom: 12, background: '#FAFBFF' }} bodyStyle={{ padding: 10 }}>
                <Form.Item label="Endereço de entrega" name="entrega_endereco"
                  rules={[{ required: true, message: 'Onde entregar?' }]}>
                  <Input placeholder="Rua, número, bairro" />
                </Form.Item>
                <Flex gap={12}>
                  <Form.Item label="UF" style={{ width: 90 }}>
                    <Select value={uf} onChange={setUf} showSearch
                      options={UFS_BR.map(u => ({ value: u, label: u }))} />
                  </Form.Item>
                  <Form.Item label="Cidade" name="entrega_cidade" style={{ flex: 1 }}
                    rules={[{ required: true, message: 'Cidade?' }]}>
                    <Select showSearch optionFilterProp="value" loading={carregandoMun}
                      placeholder="Buscar município…"
                      options={municipios.map(m => ({ value: m, label: m }))} />
                  </Form.Item>
                  <Form.Item label="CEP" name="entrega_cep" style={{ width: 130 }}>
                    <Input placeholder="00000-000" />
                  </Form.Item>
                </Flex>
                <Form.Item label="Complemento/referência" name="entrega_referencia" style={{ marginBottom: 0 }}>
                  <Input placeholder="Ex: galpão dos fundos, falar com o zelador…" />
                </Form.Item>
              </Card>
            )}
          </>
        )}

        {tipo === 'manutencao' && (
          <>
            <Form.Item label="Local" name="local" rules={[{ required: true, message: 'Onde?' }]}>
              <Input placeholder="Ex: Sala de reuniões, banheiro do 2º andar…" />
            </Form.Item>
            <Form.Item label="Problema" name="problema" rules={[{ required: true, message: 'Descreva o problema' }]}>
              <TextArea rows={3} placeholder="Ex: lâmpada queimada precisa ser trocada" />
            </Form.Item>
          </>
        )}

        {tipo === 'aviso' && (
          <>
            <Text type="secondary" style={{ fontSize: 11, display: 'block', marginBottom: 10 }}>
              Compra, aluguel ou contratação de terceiros — <strong>já realizada</strong> (ex:
              barqueiro contratado no campo; o financeiro registra a despesa) <strong>ou a
              realizar</strong> (ex: anexe a cotação em PDF pro financeiro concluir a compra).
            </Text>
            <Form.Item label="O que foi?" name="aviso_tipo" initialValue="compra"
              rules={[{ required: true }]}>
              <Radio.Group buttonStyle="solid" size="small">
                <Radio.Button value="compra">🛒 Compra</Radio.Button>
                <Radio.Button value="aluguel">📋 Aluguel</Radio.Button>
                <Radio.Button value="contratacao">👷 Contratação de terceiros</Radio.Button>
              </Radio.Group>
            </Form.Item>
            {/* Escolhe do cadastro OU digita livre. O valor continua sendo UMA
                string (o nome), igual ao que já está gravado em produção — o
                id entra num campo à parte, então nada a jusante muda de forma.
                Mesmo padrão do campo de materiais, logo acima. */}
            <Form.Item label="Fornecedor / prestador" name="fornecedor"
              tooltip="Comece a digitar: se já estiver cadastrado, escolha na lista — aí o financeiro não precisa procurar."
              rules={[{ required: true, message: 'Quem foi pago/contratado?' }]}>
              <AutoComplete
                placeholder="Ex: Anderson Werner (barqueiro)"
                options={opcoesForn}
                filterOption={filtrarFornecedor}
                onSelect={(v: string) => {
                  // NÃO usar a opção que o antd devolve aqui: o rc-select
                  // resolve a opção pelo VALUE, num Map onde o último vence
                  // (useOptions.js). Com duas filiais de mesmo nome fantasia
                  // — o caso Consplan, que é a razão de existir do
                  // utils/fornecedores.ts — clicar na primeira entregava o id
                  // da SEGUNDA, e a despesa nascia no CNPJ errado.
                  // Nome ambíguo não vira vínculo: sem id, o modal mostra
                  // "o pedido informou: X" e o financeiro escolhe a filial.
                  const iguais = fornecedores.filter((x: any) => x.nome_fantasia === v);
                  const f = iguais.length === 1 ? iguais[0] : null;
                  form.setFieldsValue({ fornecedor_id: f?.id || undefined });
                  if (f?.cnpj_cpf && !form.getFieldValue('fornecedor_cnpj')) {
                    form.setFieldsValue({ fornecedor_cnpj: formatarCnpjCpf(f.cnpj_cpf) });
                  }
                }}
                onChange={(v: string) => {
                  // Saiu do nome escolhido: o vínculo com o cadastro morre. Um
                  // id preso a um nome editado apontaria para outro fornecedor.
                  const id = form.getFieldValue('fornecedor_id');
                  if (!id) return;
                  const f = fornecedores.find((x: any) => x.id === id);
                  if (f && f.nome_fantasia === v) return;
                  const patch: any = { fornecedor_id: undefined };
                  // O CNPJ que veio junto com aquele fornecedor sai junto. Se
                  // ficasse, o modal religaria o fornecedor ANTIGO pelo CNPJ,
                  // com o nome de outro na tela.
                  const cnpjAtual = form.getFieldValue('fornecedor_cnpj');
                  if (f && cnpjAtual && formatarCnpjCpf(f.cnpj_cpf) === cnpjAtual) {
                    patch.fornecedor_cnpj = undefined;
                  }
                  form.setFieldsValue(patch);
                }} />
            </Form.Item>
            <Form.Item name="fornecedor_id" hidden><Input /></Form.Item>
            <Form.Item noStyle shouldUpdate={(a1, b1) => a1.fornecedor_id !== b1.fornecedor_id}>
              {() => (form.getFieldValue('fornecedor_id')
                ? <Text type="success" style={{ fontSize: 11, display: 'block', marginTop: -8, marginBottom: 10 }}>
                    ✓ escolhido do cadastro de fornecedores
                  </Text>
                : null)}
            </Form.Item>
            <Form.Item label="CNPJ / CPF do fornecedor (opcional)" name="fornecedor_cnpj"
              tooltip="Se souber. Ajuda o financeiro a achar (ou cadastrar) o fornecedor certo.">
              <Input placeholder="Ex: 12.345.678/0001-90"
                onBlur={(e) => {
                  const v = formatarCnpjCpf(e.target.value);
                  if (v !== e.target.value) form.setFieldsValue({ fornecedor_cnpj: v });
                }} />
            </Form.Item>
            <Flex gap={12}>
              {avisoTipo === 'compra' && (
                <Form.Item label="Unidades" name="quantidade" style={{ width: 110 }}
                  rules={[{ required: true, message: 'Quantas?' }]}>
                  <InputNumber min={1} precision={0} style={{ width: '100%' }} placeholder="Qtde" />
                </Form.Item>
              )}
              {/* No mensal o valor NÃO é o total: o Contas a Pagar repete o
                  valor cheio a cada mês. Rótulo errado aqui multiplica a
                  despesa pelo número de meses. */}
              <Form.Item name="valor" style={{ flex: 1 }}
                label={condicao === 'recorrente' ? 'Valor POR MÊS (R$)'
                  : avisoTipo === 'compra' ? 'Valor total (R$)' : 'Valor (R$)'}
                rules={[{ required: true, message: 'Valor?' }]}>
                <InputNumber min={0.01} step={0.01} decimalSeparator="," style={{ width: '100%' }} />
              </Form.Item>
              <Form.Item label="Data" name="data_compra" style={{ flex: 1 }}
                rules={[{ required: true, message: 'Quando?' }]}>
                <DatePicker format="DD/MM/YYYY" style={{ width: '100%' }} />
              </Form.Item>
            </Flex>
            {/* Obrigatórios só na contratação de terceiro NOVA: é a que se
                planeja, tem valor alto e é a que a Daniele lança sem conhecer o
                combinado. Numa compra de R$ 80 já realizada seria atrito à toa —
                e o lançamento cai na data da compra, que o pedido já traz. Na
                EDIÇÃO não se exige: pedido antigo não tem os campos, e travar
                deixaria quem quer só corrigir um typo sem conseguir salvar. */}
            <Form.Item label="Quando vence" name="data_vencimento"
              tooltip="A data limite pra pagar (a 1ª parcela, quando for parcelado). Sem ela o financeiro tem que adivinhar ou perguntar."
              rules={[{ required: avisoTipo === 'contratacao' && !ticket, message: 'Até quando tem que ser pago?' }]}>
              {/* Mexer na data-âncora refaz o cronograma pelo ritmo: senão a
                  pessoa troca a 1ª data, não vê nada mudar e não entende. */}
              <DatePicker format="DD/MM/YYYY" style={{ width: '100%' }}
                onChange={() => setVencManual(false)} />
            </Form.Item>
            <Form.Item label="Categoria" name="categoria_dre"
              tooltip="É o que faz a despesa aparecer no lugar certo do resultado da empresa e do contrato."
              rules={[{ required: avisoTipo === 'contratacao' && !ticket, message: 'Escolha a categoria' }]}
              extra={
                <Button type="link" size="small" style={{ padding: 0, height: 18, fontSize: 11 }}
                  onClick={() => setTodasCategorias(v => !v)}>
                  {todasCategorias ? '↑ mostrar só as mais comuns' : 'não achei na lista — ver todas'}
                </Button>
              }>
              <Select showSearch optionFilterProp="label" allowClear
                placeholder="Selecione a categoria da despesa"
                options={opcoesCategoria(categorias, avisoTipo, todasCategorias)} />
            </Form.Item>
            <Flex gap={12}>
              <Form.Item label="Pagamento" name="condicao" initialValue="avista" style={{ flex: 1 }}>
                <Radio.Group buttonStyle="solid" size="small">
                  {OPCOES_CONDICAO.map(o => (
                    <Radio.Button key={o.value} value={o.value}>{o.label}</Radio.Button>
                  ))}
                </Radio.Group>
              </Form.Item>
              {condicao !== 'avista' && (
                <Form.Item name="parcelas" style={{ width: 150 }}
                  label={condicao === 'recorrente' ? 'Quantos meses' : 'Em quantas vezes'}
                  initialValue={2}
                  rules={[{ required: true, message: 'Quantas?' }]}>
                  <InputNumber min={2} max={120} precision={0} suffix="×" style={{ width: '100%' }} />
                </Form.Item>
              )}
            </Flex>
            <Text type="secondary" style={{ fontSize: 11, display: 'block', marginTop: -8, marginBottom: 10 }}>
              {OPCOES_CONDICAO.find(o => o.value === condicao)?.ajuda}
            </Text>
            {/* Quando vence cada parcela. O padrão é "todo mês no mesmo dia" —
                é o que se combina de verdade. O sistema antes somava 30 dias
                corridos, e o dia ia escorregando (10/09 → 10/10 → 09/11). */}
            {condicao !== 'avista' && nParcelas >= 2 && (
              <Form.Item label="Quando vencem as parcelas"
                tooltip="Escolha o ritmo, ou ajuste data por data se o combinado foi outro.">
                <Flex gap={8} wrap="wrap" align="center">
                  <Radio.Group size="small" buttonStyle="solid" value={ritmo}
                    onChange={(e) => { setRitmo(e.target.value); setVencManual(false); }}>
                    <Radio.Button value="mensal">Todo mês no mesmo dia</Radio.Button>
                    <Radio.Button value="dias">A cada 30 dias</Radio.Button>
                  </Radio.Group>
                  <Button type="link" size="small" style={{ padding: 0, height: 20, fontSize: 12 }}
                    onClick={() => setDatasAbertas(v => !v)}>
                    {datasAbertas ? 'fechar' : 'escolher as datas'}
                  </Button>
                  {vencManual && (
                    <Button type="link" size="small" style={{ padding: 0, height: 20, fontSize: 12 }}
                      onClick={() => setVencManual(false)}>
                      voltar ao ritmo
                    </Button>
                  )}
                </Flex>
                {!primeiroVenc && (
                  <Text type="warning" style={{ fontSize: 11 }}>
                    Preencha "Quando vence" acima para ver as datas.
                  </Text>
                )}
                {!!vencimentos.length && !datasAbertas && (
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    {nParcelas}× · {resumoVencimentos(vencimentos, 5)}
                  </Text>
                )}
                {datasAbertas && (
                  <Flex gap={8} wrap="wrap" style={{ marginTop: 6 }}>
                    {vencimentos.map((d, i) => (
                      <div key={i} style={{ width: 130 }}>
                        <Text type="secondary" style={{ fontSize: 11 }}>{i + 1}/{nParcelas}</Text>
                        <DatePicker size="small" style={{ width: '100%' }} format="DD/MM/YYYY"
                          allowClear={false}
                          value={d ? dayjs(d) : null}
                          onChange={(nd) => {
                            setVencManual(true);
                            setVencimentos(atual => atual.map((x, k) =>
                              (k === i ? (nd ? nd.format('YYYY-MM-DD') : x) : x)));
                          }} />
                      </div>
                    ))}
                  </Flex>
                )}
              </Form.Item>
            )}
            <Form.Item label="Forma de pagamento" name="forma_pagamento">
              <Select allowClear placeholder="Selecione…" options={OPCOES_FORMA_PAGAMENTO} />
            </Form.Item>
            <Form.Item label="Dados para pagamento" name="pagamento_dados"
              tooltip="Chave PIX, banco/agência/conta ou CNPJ. Sem isso o financeiro não consegue pagar o prestador.">
              <Input placeholder="Ex: PIX 123.456.789-00 · ou Banco 001 ag 1234 c/c 56789-0" />
            </Form.Item>
            <Form.Item label="Centro de custo (contratos)" name="contratos">
              <Select mode="multiple" showSearch optionFilterProp="label" allowClear
                placeholder="Buscar contrato…"
                options={contratos.map(c => ({ value: c.id, label: `${c.numero} — ${c.titulo}` }))} />
            </Form.Item>
          </>
        )}

        {/* Anexos (2026-07-16): cotação/orçamento em PDF, foto do problema etc.
            Upload direto pro S3 via presigned URL; metadata vai em dados.anexos. */}
        <Form.Item label="Anexos (cotação, orçamento, foto…)">
          <Upload
            fileList={anexos.map((a, i) => ({ uid: String(i), name: a.nome, status: 'done' as const }))}
            customRequest={async ({ file, onSuccess, onError }: any) => {
              setSubindoAnexo(true);
              try {
                const token = await getToken();
                const r = await axios.post(`${API_URL}/comercial/propostas-geradas`,
                  { action: 'adm_ticket_anexo_upload_url', filename: file.name, content_type: file.type },
                  { headers: { Authorization: token } });
                const d = r.data.body ? JSON.parse(r.data.body) : r.data;
                await axios.put(d.upload_url, file, { headers: { 'Content-Type': file.type || 'application/octet-stream' } });
                setAnexos(prev => [...prev, { nome: file.name, s3_key: d.s3_key, content_type: file.type, tamanho: file.size }]);
                onSuccess?.({});
              } catch (e: any) {
                message.error('Falha no upload: ' + (e.response?.data?.error || e.message));
                onError?.(e);
              } finally { setSubindoAnexo(false); }
            }}
            onRemove={(f) => { setAnexos(prev => prev.filter((_, i) => String(i) !== f.uid)); return true; }}
            accept=".pdf,.png,.jpg,.jpeg,.doc,.docx,.xls,.xlsx"
            multiple
          >
            <Button icon={<UploadOutlined />} loading={subindoAnexo} size="small">
              Anexar arquivo
            </Button>
          </Upload>
        </Form.Item>

        <Form.Item label="Detalhes adicionais" name="descricao">
          <TextArea rows={2} placeholder="Qualquer contexto extra que ajude o administrativo…" />
        </Form.Item>
        <Form.Item label="Prioridade" name="prioridade" initialValue="media">
          <Select options={Object.entries(TICKET_PRIORIDADE_CFG).map(([k, v]) => ({ value: k, label: v.label }))}
            style={{ width: 160 }} />
        </Form.Item>
        <Button type="primary" htmlType="submit" loading={salvando} block>
          {ticket ? 'Salvar alterações' : 'Abrir pedido'}
        </Button>
      </Form>
    </Drawer>
  );
}

// ─── Página ──────────────────────────────────────────────────────────────────
function AtividadesRequisicoesInner() {
  const { message } = AntApp.useApp();
  const [searchParams, setSearchParams] = useSearchParams();
  const [meus, setMeus] = useState<Ticket[]>([]);
  const [loading, setLoading] = useState(true);
  const [novoAberto, setNovoAberto] = useState(false);
  const [editando, setEditando] = useState<Ticket | null>(null);
  const [detalhe, setDetalhe] = useState<Ticket | null>(null);
  // Exclusão com motivo (ADM-4)
  const [excluindo, setExcluindo] = useState(false);
  const [motivoExclusao, setMotivoExclusao] = useState<string | null>(null);
  const [confirmandoExclusao, setConfirmandoExclusao] = useState(false);

  const carregar = useCallback(async () => {
    setLoading(true);
    try {
      const token = await getToken();
      const r = await axios.post(`${API_URL}/comercial/propostas-geradas`,
        { action: 'adm_ticket_meus' },
        { headers: { Authorization: token } });
      const d = r.data.body ? JSON.parse(r.data.body) : r.data;
      setMeus(Array.isArray(d) ? d.filter((t: Ticket) => t.tipo !== 'ouvidoria') : []);
    } catch (e: any) {
      message.error('Falha ao carregar: ' + (e.response?.data?.error || e.message));
    } finally { setLoading(false); }
  }, [message]);
  useEffect(() => { carregar(); }, [carregar]);

  // Deep-link (sino grava no sessionStorage; Teams/menção vem em ?ticket=):
  // abre o pedido quando a lista carrega (uma vez). As duas origens seguem o
  // mesmo caminho e a chave/parâmetro some já na primeira leitura.
  const deepLinkFeito = useRef(false);
  useEffect(() => {
    if (deepLinkFeito.current || loading) return;
    const alvoUrl = searchParams.get('ticket');
    const alvo = alvoUrl || sessionStorage.getItem('requisicoes_abrir_id');
    if (!alvo) return;
    deepLinkFeito.current = true;
    sessionStorage.removeItem('requisicoes_abrir_id');
    if (alvoUrl) setSearchParams({}, { replace: true });
    const t = meus.find((x) => String(x.id) === alvo);
    if (t) { setDetalhe(t); return; }
    // Fora de "Minhas requisições" (ex.: menção a um gestor que não é dono
    // nem participante): o servidor confirma o acesso antes. Gestor vai para
    // a Central; quem não é gestor mas tem acesso abre o detalhe aqui mesmo.
    (async () => {
      try {
        const token = await getToken();
        const r = await axios.post(`${API_URL}/comercial/propostas-geradas`,
          { action: 'adm_ticket_obter', id: alvo }, { headers: { Authorization: token } });
        const d = r.data.body ? JSON.parse(r.data.body) : r.data;
        if (d?.sou_gestor) {
          window.location.assign(`/administrativo/tickets?ticket=${encodeURIComponent(alvo)}`);
          return;
        }
        const { comentarios, sou_gestor, ...ticket } = d;
        setDetalhe(ticket);
      } catch {
        message.warning('Não foi possível abrir o ticket: você não tem mais acesso ou ele foi removido.');
      }
    })();
  }, [meus, loading, searchParams, setSearchParams, message]);

  async function excluir() {
    if (!detalhe || !motivoExclusao) return;
    setConfirmandoExclusao(true);
    try {
      const token = await getToken();
      await axios.post(`${API_URL}/comercial/propostas-geradas`,
        { action: 'adm_ticket_excluir', id: detalhe.id, motivo: motivoExclusao },
        { headers: { Authorization: token } });
      message.success('Pedido excluído. O administrativo foi avisado do motivo.', 5);
      setExcluindo(false); setMotivoExclusao(null); setDetalhe(null);
      carregar();
    } catch (e: any) {
      message.error('Falha: ' + (e.response?.data?.error || e.message));
    } finally { setConfirmandoExclusao(false); }
  }

  async function abrirEdicao(t: Ticket) {
    try {
      const token = await getToken();
      const r = await axios.post(`${API_URL}/comercial/propostas-geradas`,
        { action: 'adm_ticket_obter', id: t.id }, { headers: { Authorization: token } });
      const d = r.data.body ? JSON.parse(r.data.body) : r.data;
      setEditando({ ...t, ...d });
      setDetalhe(null); setNovoAberto(true);
    } catch (e: any) {
      message.error('Não consegui carregar os convidados para editar: ' + (e.response?.data?.error || e.message));
    }
  }

  // Participante acompanha e responde, mas não edita nem cancela o pedido
  // de outra pessoa. Tickets antigos sem a flag continuam sendo do autor.
  const podeAlterar = (t: Ticket) => t.sou_dono !== false && !['concluido', 'cancelado'].includes(t.status);

  const colunas = [
    {
      title: 'Protocolo', dataIndex: 'protocolo', width: 100,
      render: (v: string) => <Text code style={{ fontSize: 11 }}>{v}</Text>,
    },
    {
      title: 'Tipo', dataIndex: 'tipo', width: 180,
      render: (v: string) => {
        const c = TICKET_TIPO_CFG[v];
        return <Text style={{ fontSize: 12 }}>{c?.emoji} {c?.label || v}</Text>;
      },
    },
    {
      title: 'Pedido', dataIndex: 'titulo', ellipsis: true,
      render: (v: string, t: Ticket) => <Space size={4}><Text strong style={{ fontSize: 12 }}>{v}</Text>
        {t.sou_participante && !t.sou_dono && <Tag color="cyan">Compartilhado</Tag>}</Space>,
    },
    {
      title: 'Prioridade', dataIndex: 'prioridade', width: 100,
      render: (v: string) => <Tag color={TICKET_PRIORIDADE_CFG[v]?.cor}>{TICKET_PRIORIDADE_CFG[v]?.label || v}</Tag>,
    },
    {
      title: 'Status', dataIndex: 'status', width: 130,
      render: (v: string) => <Tag color={TICKET_STATUS_CFG[v]?.cor}>{TICKET_STATUS_CFG[v]?.label || v}</Tag>,
    },
    {
      title: '💬', dataIndex: 'n_comentarios', width: 50,
      render: (v: number) => v > 0 ? <Text style={{ fontSize: 12 }}>{v}</Text> : null,
    },
    {
      title: 'Aberto em', dataIndex: 'criado_em', width: 100,
      render: (v: string) => <Text style={{ fontSize: 11 }}>{fmtMomentoBr(v)}</Text>,
    },
  ];

  return (
    <div style={{ padding: 16 }}>
      <Flex justify="space-between" align="center" wrap="wrap" gap={8} style={{ marginBottom: 8 }}>
        <Title level={4} style={{ margin: 0 }}>🎫 Requisições</Title>
        <Space>
          <Button size="small" icon={<ReloadOutlined />} onClick={carregar} />
          <Button type="primary" icon={<PlusOutlined />} onClick={() => { setEditando(null); setNovoAberto(true); }}>
            Novo pedido
          </Button>
        </Space>
      </Flex>
      <Text type="secondary" style={{ display: 'block', marginBottom: 14, fontSize: 12 }}>
        Abra pedidos pro administrativo: viagem de campo (hospedagem, alimentação,
        veículo), suprimentos, manutenção patrimonial — ou avise uma compra/aluguel/
        contratação já realizada. Você é notificado a cada resposta.
      </Text>

      <Table size="small" rowKey="id" columns={colunas as any} dataSource={meus}
        loading={loading} pagination={{ pageSize: 20 }}
        onRow={(t) => ({ onClick: () => setDetalhe(t), style: { cursor: 'pointer' } })}
        locale={{ emptyText: <Empty description="Você ainda não abriu nenhum pedido" /> }} />

      {/* ─── Drawer: novo pedido / edição ─── */}
      <DrawerPedido aberto={novoAberto} ticket={editando}
        onFechar={() => { setNovoAberto(false); setEditando(null); }}
        onSalvo={carregar} />

      {/* ─── Drawer: detalhe + comunicação ─── */}
      <Drawer open={!!detalhe} onClose={() => { setDetalhe(null); carregar(); }}
        title={detalhe ? `${TICKET_TIPO_CFG[detalhe.tipo]?.emoji} ${detalhe.protocolo} — ${detalhe.titulo}` : ''}
        width={560} destroyOnClose
        extra={detalhe && podeAlterar(detalhe) ? (
          <Space>
            <Button size="small" icon={<EditOutlined />}
              onClick={() => abrirEdicao(detalhe)}>
              Editar
            </Button>
            <Button size="small" danger icon={<DeleteOutlined />}
              onClick={() => { setMotivoExclusao(null); setExcluindo(true); }}>
              Excluir
            </Button>
          </Space>
        ) : undefined}>
        {detalhe && (
          <div>
            <Space size={8} wrap style={{ marginBottom: 12 }}>
              <Tag color={TICKET_STATUS_CFG[detalhe.status]?.cor}>{TICKET_STATUS_CFG[detalhe.status]?.label}</Tag>
              <Tag color={TICKET_PRIORIDADE_CFG[detalhe.prioridade]?.cor}>{TICKET_PRIORIDADE_CFG[detalhe.prioridade]?.label}</Tag>
              <Text type="secondary" style={{ fontSize: 11 }}>Aberto em {fmtMomentoBr(detalhe.criado_em)}</Text>
            </Space>
            {detalhe.sou_participante && !detalhe.sou_dono && (
              <Text type="secondary" style={{ fontSize: 12, display: 'block', marginBottom: 10 }}>
                👥 Pedido compartilhado com você. Você pode acompanhar e responder.
              </Text>
            )}
            {!!detalhe.participantes?.length && (
              <Text type="secondary" style={{ fontSize: 11, display: 'block', marginBottom: 10 }}>
                👥 Acompanham: {detalhe.participantes.map((p) => p.nome).join(', ')}
              </Text>
            )}
            {detalhe.status === 'cancelado' && detalhe.cancelado_motivo && (
              <Card size="small" style={{ marginBottom: 12, background: '#FFF1F0', borderColor: '#ffa39e' }}
                bodyStyle={{ padding: 10 }}>
                <Text style={{ fontSize: 12 }}>
                  🗑️ <strong>Excluído</strong> — motivo: {detalhe.cancelado_motivo}
                </Text>
              </Card>
            )}
            <DadosTicket dados={detalhe.dados} tipo={detalhe.tipo} ticketId={detalhe.id} />
            {detalhe.descricao && (
              <TextoComLinks texto={detalhe.descricao} style={{ display: 'block', marginBottom: 12 }} />
            )}
            <Divider style={{ margin: '12px 0' }} />
            <TicketComentarios ticketId={detalhe.id} />
          </div>
        )}
      </Drawer>

      {/* ─── Modal: excluir com motivo (ADM-4) ─── */}
      <Modal open={excluindo} onCancel={() => setExcluindo(false)}
        title={`🗑️ Excluir ${detalhe?.protocolo || 'pedido'}`}
        okText="Excluir pedido" okButtonProps={{ danger: true, disabled: !motivoExclusao, loading: confirmandoExclusao }}
        onOk={excluir} cancelText="Voltar" destroyOnClose>
        <Text style={{ fontSize: 12, display: 'block', marginBottom: 12 }}>
          O pedido sai do fluxo do administrativo, que será avisado do motivo
          (pode estar no meio do atendimento). O histórico fica registrado.
        </Text>
        <Radio.Group value={motivoExclusao} onChange={e => setMotivoExclusao(e.target.value)}
          style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {Object.entries(MOTIVOS_EXCLUSAO_TICKET).map(([k, v]) => (
            <Radio key={k} value={k}>{v}</Radio>
          ))}
        </Radio.Group>
      </Modal>
    </div>
  );
}

// Renderiza o JSONB `dados` de forma legível por tipo (reusado no kanban admin)
export function DadosTicket({ dados, tipo, ticketId }: { dados: any; tipo: string; ticketId?: string }) {
  if (!dados || Object.keys(dados).length === 0) return null;
  const linhas: Array<[string, string]> = [];
  if (tipo === 'viagem') {
    if (dados.destino) linhas.push(['Destino', dados.destino]);
    if (dados.data_ida) linhas.push(['Período', `${dayjs(dados.data_ida).format('DD/MM/YYYY')} a ${dados.data_volta ? dayjs(dados.data_volta).format('DD/MM/YYYY') : '?'}`]);
    if (dados.n_pessoas) linhas.push(['Pessoas', String(dados.n_pessoas)]);
    if (dados.equipe) linhas.push(['Equipe', dados.equipe]);
    if (dados.equipamentos?.length) linhas.push(['🧰 Equipamentos', dados.equipamentos.join('\n')]);
    if (dados.centros_custo?.length) linhas.push(['Centro de custo', dados.centros_custo.join(' + ')]);
    if (dados.precisa_alimentacao) linhas.push(['🍽️ Alimentação', 'provisionar']);
    if (dados.precisa_hotel) {
      linhas.push(['🏨 Hospedagem',
        `${dados.hotel_noites || '?'} noite(s) em ${dados.hotel_cidade || '?'}`
        + (dados.hotel_outras_cidades ? ` (alternativa: ${dados.hotel_outras_cidades})` : '')]);
      const sugestoes = Array.isArray(dados.hotel_sugestoes)
        ? dados.hotel_sugestoes
        : (dados.hotel_sugestao ? [dados.hotel_sugestao] : []);
      if (sugestoes.length) linhas.push(['Sugestões de local', sugestoes.join('\n')]);
    }
    if (dados.precisa_veiculo) {
      linhas.push(['🚙 Veículo',
        `${dados.veiculo_retirada_local || '?'}`
        + (dados.veiculo_retirada_data ? ` — ${dayjs(dados.veiculo_retirada_data).format('DD/MM')} a ${dados.veiculo_devolucao_data ? dayjs(dados.veiculo_devolucao_data).format('DD/MM/YYYY') : '?'}` : '')]);
    }
  } else if (tipo === 'suprimentos') {
    if (Array.isArray(dados.itens_lista) && dados.itens_lista.length) {
      linhas.push(['Itens', dados.itens_lista
        .map((it: any) => `${it.qtd ? `${it.qtd}${it.unidade ? ` ${it.unidade}` : 'x'} — ` : ''}${it.nome}`)
        .join('\n')]);
    } else if (dados.itens) linhas.push(['Itens', dados.itens]);
    if (dados.centros_custo?.length) linhas.push(['Centro de custo', dados.centros_custo.join(' + ')]);
    if (dados.data_necessaria) linhas.push(['Necessário até', dayjs(dados.data_necessaria).format('DD/MM/YYYY')]);
    if (dados.entrega_matriz) linhas.push(['Entrega', '🏢 Matriz da empresa']);
    else if (dados.entrega_endereco || dados.entrega_cidade) {
      linhas.push(['Entrega', [dados.entrega_endereco, dados.entrega_cidade,
        dados.entrega_cep, dados.entrega_referencia].filter(Boolean).join(' — ')]);
    } else if (dados.local_entrega) linhas.push(['Entrega', dados.local_entrega]);
  } else if (tipo === 'manutencao') {
    if (dados.local) linhas.push(['Local', dados.local]);
    if (dados.problema) linhas.push(['Problema', dados.problema]);
  } else if (tipo === 'aviso') {
    const TIPO_AVISO: Record<string, string> = { compra: '🛒 Compra', aluguel: '📋 Aluguel', contratacao: '👷 Contratação de terceiros' };
    if (dados.aviso_tipo) linhas.push(['Tipo', TIPO_AVISO[dados.aviso_tipo] || dados.aviso_tipo]);
    if (dados.quantidade) linhas.push(['Unidades', `${dados.quantidade} un.`]);
    if (dados.fornecedor) {
      linhas.push(['Fornecedor', dados.fornecedor
        + (dados.fornecedor_cnpj ? ` · ${dados.fornecedor_cnpj}` : '')
        + (dados.fornecedor_id ? ' · (do cadastro)' : '')]);
    }
    if (dados.valor != null) linhas.push(['Valor', `R$ ${parseFloat(dados.valor).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`]);
    if (dados.data_compra) linhas.push(['Data', dayjs(dados.data_compra).format('DD/MM/YYYY')]);
    if (dados.data_vencimento) linhas.push(['Vence em', dayjs(dados.data_vencimento).format('DD/MM/YYYY')]);
    if (dados.categoria_dre) linhas.push(['Categoria', dados.categoria_dre]);
    if (dados.condicao) {
      const n = dados.parcelas ? parseInt(dados.parcelas, 10) : null;
      const quando = Array.isArray(dados.vencimentos) && dados.vencimentos.length
        ? ` · vence ${dados.vencimentos
            .map((x: string) => dayjs(x).format('DD/MM'))
            .slice(0, 5).join(', ')}${dados.vencimentos.length > 5 ? ' …' : ''}`
        : '';
      linhas.push(['Condição',
        (dados.condicao === 'parcelado' ? `Parcelado em ${n || '?'}×`
         : dados.condicao === 'recorrente' ? `Mensal, ${n || '?'} meses`
         : 'À vista') + quando]);
    }
    if (dados.forma_pagamento) linhas.push(['Pagamento', dados.forma_pagamento]);
    if (dados.pagamento_dados) linhas.push(['Dados p/ pagamento', dados.pagamento_dados]);
    if (dados.centros_custo?.length) linhas.push(['Centro de custo', dados.centros_custo.join(' + ')]);
  }
  // Anexos (2026-07-16): qualquer tipo pode ter — cotação em PDF, foto etc.
  // Download via presigned GET (o admin baixa direto do kanban).
  const anexos: Array<{ nome: string; s3_key: string }> = Array.isArray(dados.anexos) ? dados.anexos : [];
  async function baixarAnexo(a: { nome: string; s3_key: string }) {
    try {
      const token = await getToken();
      const r = await axios.post(`${API_URL}/comercial/propostas-geradas`,
        { action: 'adm_ticket_anexo_download_url', id: ticketId, s3_key: a.s3_key },
        { headers: { Authorization: token } });
      const d = r.data.body ? JSON.parse(r.data.body) : r.data;
      if (d.download_url) window.open(d.download_url, '_blank');
    } catch { /* silencioso */ }
  }
  if (!linhas.length && !anexos.length) return null;
  return (
    <Card size="small" style={{ marginBottom: 12, background: '#F0F5FF' }} bodyStyle={{ padding: 10 }}>
      <Space direction="vertical" size={4} style={{ width: '100%' }}>
        {linhas.map(([k, v]) => (
          <Text key={k} style={{ fontSize: 12, whiteSpace: 'pre-wrap' }}>
            <strong>{k}:</strong> {v}
          </Text>
        ))}
        {anexos.length > 0 && (
          <div>
            <Text style={{ fontSize: 12 }}><strong>📎 Anexos:</strong></Text>
            {anexos.map((a, i) => (
              <div key={i}>
                <Button type="link" size="small" icon={<PaperClipOutlined />}
                  style={{ fontSize: 12, padding: 0, height: 20 }}
                  onClick={() => baixarAnexo(a)}>
                  {a.nome}
                </Button>
              </div>
            ))}
          </div>
        )}
      </Space>
    </Card>
  );
}

export default function AtividadesRequisicoes() {
  return <AntApp><AtividadesRequisicoesInner /></AntApp>;
}
